import * as THREE from "three";
import type { Rgb } from "./transitions";

export interface WaterMaterialControls {
  material: THREE.ShaderMaterial;
  update(time: number, wetness: number, rain: number, skyColor: Rgb, waterColor: Rgb, fogColor: Rgb, fogNear: number, fogFar: number): void;
}

export function createWaterMaterial(): WaterMaterialControls {
  const uniforms = {
    uTime: { value: 0 },
    uWetness: { value: 0.7 },
    uRain: { value: 0 },
    uSkyColor: { value: new THREE.Color() },
    uWaterColor: { value: new THREE.Color() },
    uFogColor: { value: new THREE.Color() },
    uFogNear: { value: 20 },
    uFogFar: { value: 300 },
  };

  const material = new THREE.ShaderMaterial({
    name: "JiangnanWater",
    uniforms,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    vertexShader: `
      uniform float uTime;
      varying vec3 vWorldPosition;
      varying vec3 vViewDirection;
      varying vec3 vWaveNormal;

      void main() {
        vec3 p = position;
        float waveA = sin(p.x * 0.23 + uTime * 0.72) * 0.035;
        float waveB = sin(p.z * 0.31 - uTime * 0.48 + p.x * 0.08) * 0.025;
        p.y += waveA + waveB;
        vec3 objectNormal = normalize(vec3(
          -cos(p.x * 0.23 + uTime * 0.72) * 0.008,
          1.0,
          -cos(p.z * 0.31 - uTime * 0.48 + p.x * 0.08) * 0.008
        ));
        vec4 world = modelMatrix * vec4(p, 1.0);
        vWorldPosition = world.xyz;
        vViewDirection = cameraPosition - world.xyz;
        vWaveNormal = normalize(mat3(modelMatrix) * objectNormal);
        gl_Position = projectionMatrix * viewMatrix * world;
      }
    `,
    fragmentShader: `
      uniform float uTime;
      uniform float uWetness;
      uniform float uRain;
      uniform vec3 uSkyColor;
      uniform vec3 uWaterColor;
      uniform vec3 uFogColor;
      uniform float uFogNear;
      uniform float uFogFar;
      varying vec3 vWorldPosition;
      varying vec3 vViewDirection;
      varying vec3 vWaveNormal;

      float hash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
      }

      float noise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
          mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0)), f.x), f.y);
      }

      void main() {
        vec3 viewDirection = normalize(vViewDirection);
        float fresnel = pow(1.0 - max(dot(viewDirection, vWaveNormal), 0.0), 3.0);
        float fineRipple = sin(vWorldPosition.x * 1.75 + vWorldPosition.z * 1.37 + uTime * 1.35) * 0.014;
        fineRipple += sin(vWorldPosition.x * -0.62 + vWorldPosition.z * 2.15 - uTime * 0.82) * 0.009;
        float rainRipple = smoothstep(0.9, 0.995, sin(length(fract(vWorldPosition.xz * 0.29) - 0.5) * 28.0 - uTime * 3.4));
        rainRipple *= uRain * hash(floor(vWorldPosition.xz * 0.29));
        float reflectionBreakup = noise(vWorldPosition.xz * 0.19 + vec2(uTime * 0.018, -uTime * 0.012));
        reflectionBreakup = smoothstep(0.24, 0.78, reflectionBreakup + fineRipple * 5.0);
        vec3 refracted = uWaterColor * (0.7 + fineRipple);
        vec3 reflected = mix(uSkyColor, uWaterColor, 0.38) * (0.86 + reflectionBreakup * 0.2);
        float reflectionAmount = (0.16 + fresnel * 0.56) * (0.72 + reflectionBreakup * 0.28);
        vec3 color = mix(refracted, reflected, reflectionAmount);
        color += rainRipple * vec3(0.04, 0.052, 0.046);
        float distanceToCamera = length(vViewDirection);
        float fogAmount = smoothstep(uFogNear, uFogFar, distanceToCamera);
        color = mix(color, uFogColor, fogAmount);
        gl_FragColor = vec4(color, 0.82 + fresnel * 0.12);
      }
    `,
  });

  return {
    material,
    update(time, wetness, rain, skyColor, waterColor, fogColor, fogNear, fogFar) {
      uniforms.uTime.value = time;
      uniforms.uWetness.value = wetness;
      uniforms.uRain.value = rain;
      uniforms.uSkyColor.value.setRGB(...skyColor);
      uniforms.uWaterColor.value.setRGB(...waterColor);
      uniforms.uFogColor.value.setRGB(...fogColor);
      uniforms.uFogNear.value = fogNear;
      uniforms.uFogFar.value = fogFar;
    },
  };
}
